<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div v-if="shifts.length" class="rd-chips" role="group" :aria-label="$t('shifts')">
        <button
          v-for="shift in shifts"
          :key="shift"
          type="button"
          class="rd-chip"
          :aria-pressed="String(selectedShift === shift)"
          @click="selectedShift = shift"
        >
          <report-shift-tag :shift="Number(shift)" long></report-shift-tag>
        </button>
      </div>
      <div class="rd-seg" role="tablist" :aria-label="$t('view')">
        <button
          v-for="layout in layoutTabs"
          :key="layout.value"
          type="button"
          role="tab"
          :aria-selected="String(selectedLayout === layout.value)"
          @click="selectedLayout = layout.value"
        >
          {{ layout.text }}
        </button>
      </div>
    </report-tools>

    <report-state v-if="isRange" icon="mdi-calendar-today" :title="$t('daily_report')"></report-state>
    <report-state v-else-if="dayLoading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card gm-scroll">
      <table class="rd-tbl gm-orderers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col">{{ $t("name_surname") }}</th>
            <th v-for="category in mealCategories" :key="category" scope="col">{{ dayCategoryLabels[category] }}</th>
            <th scope="col">{{ $t("note") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="`ID ${row.userId}`">{{ row.fullname }}</td>
            <td v-for="category in mealCategories" :key="category" class="gm-orderers__cell">
              <span v-for="(meal, mealIndex) in row.cells[category]" :key="mealIndex" class="gm-orderers__line">
                <b v-if="selectedLayout !== 1" class="rd-num">{{ meal.quantity }}<template v-if="selectedLayout === 0">×</template></b>
                <span v-if="selectedLayout !== 2">{{ meal.name }}</span>
              </span>
              <span v-if="!row.cells[category].length" class="rd-dash" aria-hidden="true">–</span>
            </td>
            <td class="gm-orderers__note">{{ row.note }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-for="category in mealCategories" :key="category" class="rd-num">{{ totals[category] || 0 }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyReportTabCustomers from "@/components/company/reports/tabs/CompanyReportTabCustomers.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasCompanyGroupOrdererRows from "../../hasCompanyGroupOrdererRows";

/**
 * Naručioci firme tipa "grupa" na računaru: tabela radnika izabrane smene (oznake smena u alatima) sa obrocima po
 * grupama jela i napomenom; prikaz je količine i nazivi / nazivi / količine. Radi samo za jedan dan. Firme i
 * pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopGroupOrderers",
  extends: CompanyReportTabCustomers,
  mixins: [hasCompanyGroupOrdererRows],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    layoutTabs() {
      // četvrti izbor starog prikaza ("Rezervne obroke") nikad nije imao prikaz, pa ga ovde nema
      return this.layouts.filter((layout) => layout.value < 3);
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_orders_found");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.rows.length, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: this.mealTotal },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    dayLoading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = ["#", this.$t("name_surname"), ...this.mealCategories.map((category) => this.dayCategoryLabels[category]), this.$t("note")];
      const lines = (meals) =>
        meals
          .map((meal) => (this.selectedLayout === 2 ? meal.quantity : this.selectedLayout === 1 ? meal.name : `${meal.quantity}× ${meal.name}`))
          .join("; ");
      const rows = this.rows.map((row, index) => [
        index + 1,
        row.fullname,
        ...this.mealCategories.map((category) => lines(row.cells[category])),
        row.note,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
